import { Component, ChangeDetectionStrategy } from '@angular/core';
import { AddDocumentDialogService } from '../../document-tracking/add-document-dialog/add-document-dialog.service';
import { FormControl, FormGroup } from '@angular/forms';
import VOwnedEntitiesMap from '../../../interfaces/v-owned-entities-map';
import { combineLatest, debounceTime, Observable, Subscription } from 'rxjs';
import { DialogRef } from '@angular/cdk/dialog';
import { DocumentRequirementsService } from '../document-requirements.service';
import { CurrentUserService } from '../../../services/current-user.service';
import { UpsertDocRequirementInput } from '../graphql/document-requirements';
import { formatDate } from '@angular/common';
import { DefaultSnackBarService } from '../../../services/default-snack-bar.service';

@Component({
    selector: 'app-add-document',
    templateUrl: './add-document.component.html',
    styleUrls: ['./add-document.component.scss'],
    providers: [AddDocumentDialogService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddDocumentComponent {
  public formControls = new FormGroup({
    entity_name: new FormControl<string | VOwnedEntitiesMap | null>(null),
    entity_type: new FormControl<string | null>(null),
    return_requirement: new FormControl<boolean | null>(true),
    audit_requirement: new FormControl<boolean | null>(true),
    audit_draft_due: new FormControl<Date | string | null>(null),
    audit_final_due: new FormControl<Date | string | null>(null),
    return_draft_due: new FormControl<Date | string | null>(null),
    return_final_due: new FormControl<Date | string | null>(null),
  });
  nameSub: Subscription;
  constructor(
    public dialogRef: DialogRef<AddDocumentComponent>,
    public documentDialogService: AddDocumentDialogService,
    public requirementsService: DocumentRequirementsService,
    public userService: CurrentUserService,
    private _snackBar: DefaultSnackBarService
  ) {}
  ngOnInit() {
    this.nameSub = this.formControls.controls.entity_name.valueChanges
      .pipe(debounceTime(500))
      .subscribe((value) => {
        if (
          typeof value === 'string' &&
          this.formControls.controls.entity_type.value
        ) {
          this.documentDialogService.searchEntities(
            this.formControls.controls.entity_type.value,
            value
          );
        } else if (value === null) {
          this.documentDialogService.clearOptions();
        }
      });
  }

  displayFn(entityMap: VOwnedEntitiesMap) {
    if (entityMap) {
      let relatedString = '';
      let entityString = '';
      if (entityMap.related_name?.length) {
        relatedString = ` [${entityMap.related_name}]`;
      }
      if (entityMap.owned_entity_name?.length) {
        entityString = `${entityMap.owned_entity_name}`;
      } else {
        entityString = 'Unknown Entity';
      }
      return `${entityString}${relatedString}`;
    }
    return '';
  }
  getRequirement(name: string): boolean | null {
    if (name === 'Audit') {
      return this.formControls.controls.audit_requirement.value;
    } else if (name === 'Tax Return') {
      return this.formControls.controls.return_requirement.value;
    }
    return null;
  }
  getDueDate(type: string, subType: string): string | null {
    let date: Date | string | null = null;
    if (type === 'Audit') {
      if (subType === 'Draft') {
        date = this.formControls.controls.audit_draft_due.value;
      } else if (subType === 'Final') {
        date = this.formControls.controls.audit_final_due.value;
      }
    } else if (type === 'Tax Return') {
      if (subType === 'Draft') {
        date = this.formControls.controls.return_draft_due.value;
      } else if (subType === 'Final') {
        date = this.formControls.controls.return_final_due.value;
      }
    }
    if (date) {
      return formatDate(date, 'yyyy-MM-dd', 'en-US');
    }

    return null;
  }
  getInputKey(
    type: string,
    subType: string
  ):
    | keyof {
        audit_draft: UpsertDocRequirementInput | null;
        audit_final: UpsertDocRequirementInput | null;
        return_draft: UpsertDocRequirementInput | null;
        return_final: UpsertDocRequirementInput | null;
      }
    | null {
    if (type === 'Audit') {
      if (subType === 'Draft') {
        return 'audit_draft';
      } else if (subType === 'Final') {
        return 'audit_final';
      }
    } else if (type === 'Tax Return') {
      if (subType === 'Draft') {
        return 'return_draft';
      } else if (subType === 'Final') {
        return 'return_final';
      }
    }
    return null;
  }
  saveRequirements() {
    let data = this.formControls.value;
    let inputs: UpsertDocRequirementInput[] = [];
    let sub: Subscription;
    if (data.entity_name && typeof data.entity_name !== 'string') {
      let entity = data.entity_name as VOwnedEntitiesMap;
      sub = combineLatest({
        user: this.userService.user$,
        docTypes: this.requirementsService.docTypes$,
      }).subscribe(({ user, docTypes }) => {
        if (docTypes?.length && user?.id) {
          for (let i = 0; i < docTypes.length; i++) {
            let docType = docTypes[i];
            if (docType.name === 'Audit' || docType.name === 'Tax Return') {
              for (let j = 0; j < docType.docSubtypes.length; j++) {
                let subType = docType.docSubtypes[j];
                if (subType.name === 'Draft' || subType.name === 'Final') {
                  let input: UpsertDocRequirementInput = {
                    owned_entity_id: entity.owned_entity_id,
                    doc_type_id: docType.id,
                    doc_subtype_id: subType.id,
                    user_id: user.id,
                    requirement_flag: this.getRequirement(docType.name),
                    due_date: this.getDueDate(docType.name, subType.name),
                  };
                  let key = this.getInputKey(docType.name, subType.name);
                  if (key) {
                    inputs.push(input);
                  }
                }
              }
            }
          }
        }
        let mutateSub: Subscription;
        mutateSub = this.requirementsService
          .upsertRequirements$(inputs)
          .subscribe(() => {
            this._snackBar.open('Added recurring documents');
            this.dialogRef.close();
            mutateSub?.unsubscribe();
          });
        sub?.unsubscribe();
      });
    }
  }

  ngOnDestroy() {
    this.nameSub?.unsubscribe();
  }
}
